// v2/config/configuration-service.ts — Singleton ConfigurationService
// Central access point for all configuration data

import { Action, ApplicationState, ProductType, Role } from '../shared/types/domain';
import {
  DefaultPermissions,
  FormFieldSchema,
  PermissionMatrix,
  StatusDisplayConfig,
  StepDefinition,
  WorkflowDefinition,
} from './types';
import { PRODUCT_TYPE_PERMISSIONS, DEFAULT_ROLE_PERMISSIONS } from './permissions.config';
import { PRODUCT_TYPE_WORKFLOWS } from './workflows.config';
import { FORM_SCHEMAS, STEP_DEFINITIONS } from './forms.config';
import { FEATURE_FLAGS, STATUS_DISPLAY_CONFIG } from './feature-flags.config';

export interface ConfigurationService {
  // Permissions
  getPermissionMatrix(productType: ProductType): PermissionMatrix;
  getDefaultPermissions(role: Role): Action[];

  // Workflows
  getWorkflowDefinition(productType: ProductType): WorkflowDefinition;
  getAvailableTransitions(
    productType: ProductType,
    currentState: ApplicationState,
    role: Role,
  ): ApplicationState[];

  // Forms
  getFormSchema(formName: string, _productType?: ProductType): FormFieldSchema[];
  getStepDefinitions(formName: string): StepDefinition[];

  // Feature Flags
  isFeatureEnabled(featureName: string): boolean;

  // Status display
  getStatusDisplayConfig(): Record<ApplicationState, StatusDisplayConfig>;
}

export const configurationService: ConfigurationService = {
  // ─── Permissions ─────────────────────────────────────────────────────────

  getPermissionMatrix(productType: ProductType): PermissionMatrix {
    return PRODUCT_TYPE_PERMISSIONS[productType];
  },

  getDefaultPermissions(role: Role): Action[] {
    return (DEFAULT_ROLE_PERMISSIONS as DefaultPermissions)[role] ?? [];
  },

  // ─── Workflows ───────────────────────────────────────────────────────────

  getWorkflowDefinition(productType: ProductType): WorkflowDefinition {
    return PRODUCT_TYPE_WORKFLOWS[productType];
  },

  getAvailableTransitions(
    productType: ProductType,
    currentState: ApplicationState,
    role: Role,
  ): ApplicationState[] {
    const workflow = PRODUCT_TYPE_WORKFLOWS[productType];
    return workflow.transitions
      .filter((t) => t.from === currentState && t.allowedRoles.includes(role))
      .map((t) => t.to);
  },

  // ─── Forms ───────────────────────────────────────────────────────────────

  getFormSchema(formName: string, _productType?: ProductType): FormFieldSchema[] {
    return FORM_SCHEMAS[formName] ?? [];
  },

  getStepDefinitions(formName: string): StepDefinition[] {
    return STEP_DEFINITIONS[formName] ?? [];
  },

  // ─── Feature Flags ───────────────────────────────────────────────────────

  isFeatureEnabled(featureName: string): boolean {
    return FEATURE_FLAGS[featureName] ?? false;
  },

  // ─── Status Display ──────────────────────────────────────────────────────

  getStatusDisplayConfig(): Record<ApplicationState, StatusDisplayConfig> {
    return STATUS_DISPLAY_CONFIG;
  },
};
